// PillarGrid — the "Start here" map shown beneath the home hero.
//
// Five large cards, one per IA pillar, each with a one-line value prop
// and a single live stat pulled from existing hooks/queries. The goal:
// every meaningful surface in the app is visible and tappable in <2s
// after landing, without cluttering the hero search.
//
// Stats are best-effort — if a query fails we show the static label
// only (never a number that lies). All counts are read-only.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, MessageSquare, Bot, Wrench, Library as LibraryIcon } from "lucide-react";
import { useMasteryJourney } from "@/hooks/useMasteryJourney";
import { useMyAgents } from "@/hooks/useAgents";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AI_MASTERY_PATH } from "@/data/aiMasteryPath";

type Pillar = {
  to: string;
  /** When set, clicking scrolls to this element id on the current page
   *  and focuses the first input inside it instead of navigating. */
  scrollTo?: string;
  label: string;
  tagline: string;
  Icon: typeof MessageSquare;
  stat: string;
};

const PillarGrid = () => {
  const { user } = useAuth();
  const { journey } = useMasteryJourney();
  const { data: agents } = useMyAgents();
  const [libraryCount, setLibraryCount] = useState<number | null>(null);

  // Lightweight count of public library entries — head:true skips
  // payload, so this is one cheap round-trip. Failures fall back to
  // a static label.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { count, error } = await supabase
        .from("library_entries")
        .select("*", { count: "exact", head: true })
        .eq("visibility", "public");
      if (!cancelled && !error && typeof count === "number") setLibraryCount(count);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const skillsTotal = AI_MASTERY_PATH.length || 100;
  const skillsCompleted = journey.completedSlugs.length;
  const skillsLaunched = journey.launchedSkills.length;
  const learnStat = user
    ? `${skillsCompleted + skillsLaunched}/${skillsTotal} skills started`
    : `${skillsTotal} skills mapped`;

  const agentStat = user
    ? agents && agents.length > 0
      ? `${agents.length} saved`
      : "Build your first"
    : "Templates ready";

  const libraryStat =
    typeof libraryCount === "number"
      ? `${libraryCount} shared playbooks`
      : "Browse community work";

  const pillars: Pillar[] = [
    {
      to: "/roadmap",
      label: "Learn",
      tagline: "Walk the 100-step Path to AI Mastery.",
      Icon: Compass,
      stat: learnStat,
    },
    {
      to: "/",
      scrollTo: "home-hero-chat-anchor",
      label: "Do",
      tagline: "Run a playbook in chat — picks the right model for each step.",
      Icon: MessageSquare,
      stat: "Jump to chat ↑",
    },
    {
      to: "/agents",
      label: "Build",
      tagline: "Spin up an agent that does the work for you.",
      Icon: Bot,
      stat: agentStat,
    },
    {
      to: "/directory",
      label: "Discover",
      tagline: "Browse, compare, and pick the best AI tool for the job.",
      Icon: Wrench,
      stat: "Catalogue + compare",
    },
    {
      to: "/library",
      label: "Share",
      tagline: "Fork, remix, and publish playbooks with the community.",
      Icon: LibraryIcon,
      stat: libraryStat,
    },
  ];

  return (
    <section
      aria-labelledby="pillars-heading"
      className="container mx-auto px-4 py-10 md:py-14"
    >
      <div className="flex items-baseline justify-between mb-5">
        <h2
          id="pillars-heading"
          className="font-mono text-sm uppercase tracking-wider text-muted-foreground"
        >
          Start here
        </h2>
        <span className="text-[10px] font-mono text-muted-foreground/60 hidden sm:inline">
          Five ways to use HackerLabs
        </span>
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {pillars.map(({ to, scrollTo, label, tagline, Icon, stat }) => {
          const cardClass =
            "group relative flex flex-col h-full w-full text-left rounded-lg border border-border bg-card/40 hover:bg-card hover:border-primary/50 transition-colors p-4 focus:outline-none focus:ring-2 focus:ring-ring";
          const inner = (
            <>
              <Icon className="w-5 h-5 text-primary mb-3" aria-hidden />
              <span className="font-mono font-semibold text-sm text-foreground">
                {label}
              </span>
              <span className="mt-1 text-xs text-muted-foreground leading-snug flex-1">
                {tagline}
              </span>
              <span className="mt-3 inline-block text-[10px] font-mono uppercase tracking-wider text-primary/80 group-hover:text-primary">
                {stat}
              </span>
            </>
          );
          return (
            <li key={label}>
              {scrollTo ? (
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById(scrollTo);
                    if (!el) return;
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    setTimeout(() => {
                      const field = el.querySelector<HTMLElement>(
                        "input, textarea, [contenteditable='true']",
                      );
                      field?.focus();
                    }, 350);
                  }}
                  className={cardClass}
                  aria-label={`${label}: ${tagline}`}
                >
                  {inner}
                </button>
              ) : (
                <Link to={to} className={cardClass} aria-label={`${label}: ${tagline}`}>
                  {inner}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default PillarGrid;